import Image from "next/image";
import { Mail, MapPin } from "lucide-react";
import logo from "@/assets/logos/alertai_icon_light.svg";
import { footerColumns, site } from "@/data/site";

type FooterLink = {
  label: string;
  href: string;
  track?: string;
  trackLabel?: string;
};

export default function Footer() {
  return (
    <footer className="relative overflow-hidden border-t border-line/10 bg-ink">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-electric/50 to-transparent"
      />
      <div className="container-x relative pb-10 pt-16 sm:pt-20">
        <div className="grid gap-12 lg:grid-cols-[1.4fr_2fr]">
          <div className="max-w-sm">
            <a href="/" aria-label="Alert AI — página inicial" className="inline-block">
              <Image src={logo} alt="Alert AI" className="h-8 w-auto" />
            </a>
            <p className="mt-5 text-base leading-relaxed text-muted">
              {site.slogan}
            </p>
            <ul className="mt-6 space-y-3 text-sm text-muted">
              <li className="flex items-start gap-2.5">
                <Mail className="mt-0.5 h-4 w-4 shrink-0 text-electric" aria-hidden="true" />
                <a
                  href={`mailto:${site.email}`}
                  className="transition-colors hover:text-fg"
                >
                  {site.email}
                </a>
              </li>
              <li className="flex items-start gap-2.5">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-electric" aria-hidden="true" />
                <span>
                  {site.legalName} · {site.address.city} — {site.address.region}
                </span>
              </li>
            </ul>
          </div>

          <div className="grid grid-cols-2 gap-10 sm:grid-cols-3">
            {footerColumns.map((col) => (
              <nav key={col.title} aria-label={col.title}>
                <h2 className="text-xs font-semibold uppercase tracking-[0.14em] text-fg">
                  {col.title}
                </h2>
                <ul className="mt-5 space-y-3">
                  {(col.links as readonly FooterLink[]).map((l) => (
                    <li key={l.label}>
                      <a
                        href={l.href}
                        data-track={l.track}
                        data-track-label={l.trackLabel}
                        className="text-sm text-muted transition-colors hover:text-fg focus-visible:text-fg"
                      >
                        {l.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </nav>
            ))}
          </div>
        </div>

        {/* Wordmark decorativo */}
        <div
          aria-hidden="true"
          className="select-none pt-16 text-center text-[clamp(4rem,17vw,13rem)] font-bold leading-none tracking-[-0.06em] text-transparent"
          style={{
            backgroundImage:
              "linear-gradient(180deg, rgb(148 175 255 / 0.22), rgb(148 175 255 / 0))",
            WebkitBackgroundClip: "text",
            backgroundClip: "text",
          }}
        >
          Alert AI
        </div>

        <div className="mt-2 flex flex-col items-center justify-between gap-3 border-t border-line/10 pt-6 text-xs text-muted sm:flex-row">
          <p>
            © {new Date().getFullYear()} Alert AI — {site.legalName} Todos os direitos reservados.
          </p>
          <a href={site.privacyUrl} className="transition-colors hover:text-fg">
            Privacidade e LGPD
          </a>
        </div>
      </div>
    </footer>
  );
}
